import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '@ipreo/northstar';
import { Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { Allotment } from '../types/allotment.types';
import { Deal } from '../types/deal.types';
import {
  DesignationBreakdown,
  DesignationBreakdownPayload,
  DesignationDealInfo,
  DesignationEntryStatus,
  DesignationIssue
} from '../types/designation.types';

@Injectable()
export class DesignationService {
  private apiHost: string;

  constructor(
    private http: HttpClient,
    @Inject(ENVIRONMENT_CONFIG) environmentConfig: EnvironmentConfig
  ) {
    this.apiHost = environmentConfig.params.apiHost as string;
  }

  public getAllotmentsById({
    deal
  }: {
    deal: Deal;
  }): Observable<Allotment[] | Error> {
    const { id } = deal;
    const url = `${this.apiHost}/v1/buyside-desi/issues/${id}/allotments`;
    const errorMessage = `Unable to load Allotments for ${deal.published.description}.`;

    return this.http.get(url, { observe: 'response' }).pipe(
      mergeMap((response: HttpResponse<any>) => {
        const allotments = response.status === 200 && response.body;
        return allotments ? of(allotments) : throwError(Error(errorMessage));
      }),
      catchError(() => throwError(Error(errorMessage)))
    );
  }

  public getDesignationIssues(): Observable<DesignationIssue[] | Error> {
    const url = `${this.apiHost}/v1/buyside-desi/issues`;
    const errorMessage = 'Unable to load the deals due for designations.';

    return this.http.get(url, { observe: 'response' }).pipe(
      mergeMap((response: HttpResponse<any>) => {
        const designationIssues = response.status === 200 && response.body;

        return designationIssues
          ? of(designationIssues)
          : throwError(Error(errorMessage));
      }),
      catchError(() => throwError(Error(errorMessage)))
    );
  }

  public getDesignationDealInfoById({
    deal
  }: {
    deal: Deal;
  }): Observable<DesignationDealInfo | Error> {
    const { id } = deal;
    const url = `${this.apiHost}/v1/buyside-desi/issues/${id}`;
    const errorMessage = `Unable to load Priority policy and Syndicate list for ${deal.published.description}.`;

    return this.http.get(url, { observe: 'response' }).pipe(
      mergeMap((response: HttpResponse<any>) => {
        const designationDealInfo = response.status === 200 && response.body;
        return designationDealInfo
          ? of(designationDealInfo)
          : throwError(Error(errorMessage));
      }),
      catchError(() => throwError(Error(errorMessage)))
    );
  }

  public submitDesignationBreakdown(
    designationBreakdown: DesignationBreakdownPayload,
    id: string
  ): Observable<DesignationBreakdown[] | Error> {
    const url = `${this.apiHost}/v1/buyside-desi/issues/${id}/desi-breakdowns/bulk-upsert`;
    const defaultErrMessage = 'Error submitting the designation breakdowns.';

    return this.http
      .post(url, designationBreakdown, { observe: 'response' })
      .pipe(
        mergeMap((response: HttpResponse<any>) => {
          const successStatus = response.status === 200 && response.body;
          return successStatus
            ? of(successStatus)
            : throwError(Error(defaultErrMessage));
        }),
        catchError((error) => {
          switch (error.status) {
            case 400:
            default:
              return throwError(Error(defaultErrMessage));
            case 409:
            case 422: {
              error.message = defaultErrMessage;
              return throwError(error);
            }
          }
        })
      );
  }

  public getDesiBreakdownsByGii(
    deal: Deal
  ): Observable<DesignationBreakdown[] | Error> {
    const { id } = deal;
    const url = `${this.apiHost}/v1/buyside-desi/issues/${id}/desi-breakdowns`;
    const errorMessage = `Unable to load designation breakdown data for ${deal.published.description}.`;

    return this.http.get(url, { observe: 'response' }).pipe(
      mergeMap((response: HttpResponse<any>) => {
        const successStatus = response.status === 200 && response.body;
        return successStatus
          ? of(successStatus)
          : throwError(Error(errorMessage));
      }),
      catchError(() => throwError(Error(errorMessage)))
    );
  }

  public getDesiEntryStatusByGii(
    deal: Deal
  ): Observable<DesignationEntryStatus | Error> {
    const { id } = deal;
    const url = `${this.apiHost}/v1/buyside-desi/issues/${id}/desi-entry-status`;
    const errorMessage = `Unable to load BM designation permissions for ${deal.published.description}.`;

    return this.http.get(url, { observe: 'response' }).pipe(
      mergeMap((response: HttpResponse<any>) => {
        const successStatus = response.status === 200 && response.body;

        return successStatus
          ? of(successStatus)
          : throwError(Error(errorMessage));
      }),
      catchError((error) =>
        error.status === 404 ? of({}) : throwError(Error(errorMessage))
      )
    );
  }
}
